<template>
  <div class="space-y-6">
    <div class="text-center">
      <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      </div>
      <h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
        Import Your Existing Website
      </h2>
      <p class="mt-2 text-gray-600 dark:text-gray-300 max-w-md mx-auto">
        Enter your current website URL and we'll automatically extract your business information, services, and style.
      </p>
    </div>

    <div class="max-w-lg mx-auto">
      <label for="website-url" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
        Website URL
      </label>
      <div class="flex gap-3">
        <div class="relative flex-1">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </div>
          <input
            id="website-url"
            v-model="url"
            type="url"
            class="block w-full pl-10 pr-3 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            placeholder="https://yourwebsite.com"
            :disabled="disabled"
            @keyup.enter="handleImport">
        </div>
        <button
          type="button"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="!isValidUrl || disabled"
          @click="handleImport">
          <svg v-if="disabled" class="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {{ disabled ? 'Importing...' : 'Import' }}
        </button>
      </div>

      <p v-if="error" class="mt-2 text-sm text-red-600 dark:text-red-400">
        {{ error }}
      </p>
    </div>

    <!-- What we'll extract -->
    <div class="max-w-2xl mx-auto mt-8">
      <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
        What we'll extract:
      </p>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span class="text-xs text-gray-600 dark:text-gray-300">Business Info</span>
        </div>
        <div class="flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span class="text-xs text-gray-600 dark:text-gray-300">Services</span>
        </div>
        <div class="flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          <span class="text-xs text-gray-600 dark:text-gray-300">Colors & Style</span>
        </div>
        <div class="flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span class="text-xs text-gray-600 dark:text-gray-300">Contact Info</span>
        </div>
      </div>
    </div>

    <!-- Or — pick a curated template -->
    <div class="max-w-2xl mx-auto mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
      <div class="text-center mb-4">
        <span class="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500">or</span>
      </div>
      <button
        type="button"
        class="w-full flex items-start gap-4 p-5 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
        @click="$emit('choose-template')">
        <span class="flex-shrink-0 w-10 h-10 bg-primary-100 dark:bg-primary-900/40 rounded-lg flex items-center justify-center">
          <svg class="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="3" y="3" width="7" height="7" rx="1" stroke-width="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1" stroke-width="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1" stroke-width="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1" stroke-width="1.5" />
          </svg>
        </span>
        <span class="flex-1">
          <span class="block font-semibold text-gray-900 dark:text-gray-100">Start from a curated template</span>
          <span class="block text-sm text-gray-600 dark:text-gray-300 mt-0.5">Pick a hand-crafted, designer-built site for your industry.</span>
        </span>
        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <div class="text-center pt-4">
      <button
        type="button"
        class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
        @click="$emit('skip')">
        Skip and enter information manually
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  error: { type: String, default: null }
})

const emit = defineEmits(['import', 'skip', 'choose-template'])

const url = ref('')

const isValidUrl = computed(() => {
  if (!url.value) return false
  try {
    const testUrl = url.value.startsWith('http') ? url.value : `https://${url.value}`
    new URL(testUrl)
    return true
  } catch {
    return false
  }
})

function handleImport() {
  if (isValidUrl.value && !props.disabled) {
    emit('import', url.value)
  }
}
</script>
